import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import {
  LayoutDashboard, ArrowLeftRight, Upload, Landmark, FileBarChart,
  Building2, Users, Settings, LogOut, ChevronDown, Check, Menu, Wallet, Repeat, ScrollText, Inbox, FolderOpen, Banknote,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "view_dashboard" },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, perm: "view_dashboard" },
  { to: "/import", label: "Import Statement", icon: Upload, perm: "import_statements" },
  { to: "/accounts", label: "Accounts", icon: Landmark, perm: "view_dashboard" },
  { to: "/recurring", label: "Recurring", icon: Repeat, perm: "view_dashboard" },
  { to: "/reports", label: "Reports", icon: FileBarChart, perm: "view_reports" },
  { to: "/statement", label: "Statement", icon: ScrollText, perm: "view_reports" },
  { to: "/company", label: "Company Profile", icon: Building2, perm: "view_dashboard" },
  { to: "/enquiries", label: "Enquiries", icon: Inbox, perm: "view_dashboard" },
  { to: "/documents", label: "Documents", icon: FolderOpen, perm: "view_dashboard" },
  { to: "/bank-statements", label: "Bank Statements", icon: Banknote, perm: "view_dashboard" },
  { to: "/users", label: "Users & Permissions", icon: Users, perm: "__super__" },
  { to: "/settings", label: "Settings", icon: Settings, perm: "view_dashboard" },
];

const MOBILE_NAV = NAV.filter((n) => ["/dashboard", "/transactions", "/import", "/reports"].includes(n.to));

function CompanySwitcher() {
  const { companies, company, selectCompany } = useApp();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="company-switcher"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-indigo-300 transition-colors max-w-[220px]"
      >
        <div className="w-7 h-7 rounded-lg bg-indigo-600 text-white grid place-items-center text-xs font-bold shrink-0 overflow-hidden">
          {company?.logo_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`} alt="" className="w-full h-full object-cover" />
          ) : (
            (company?.name || "S").slice(0, 1)
          )}
        </div>
        <span className="text-sm font-semibold text-slate-800 truncate">{company?.name || "Select company"}</span>
        <ChevronDown size={16} className="text-slate-400 shrink-0" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-lg z-30 p-1.5">
            {companies.map((c) => (
              <button
                key={c.id}
                data-testid={`company-option-${c.id}`}
                onClick={() => { selectCompany(c.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 text-left"
              >
                <span className="flex-1 text-sm font-medium text-slate-700 truncate">{c.name}</span>
                {c.id === company?.id && <Check size={16} className="text-indigo-600" />}
              </button>
            ))}
            {companies.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No companies</div>}
          </div>
        </>
      )}
    </div>
  );
}

function CurrencyToggle() {
  const { currency, toggleCurrency } = useApp();
  return (
    <div className="flex items-center bg-slate-100 rounded-xl p-0.5" data-testid="currency-toggle">
      {["INR", "USD"].map((c) => (
        <button
          key={c}
          data-testid={`currency-${c}`}
          onClick={() => toggleCurrency(c)}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            currency === c ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"
          }`}
        >
          {c === "INR" ? "\u20B9 INR" : "$ USD"}
        </button>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);

  const visibleNav = NAV.filter((n) =>
    n.perm === "__super__" ? user?.role === "super_admin" : hasPermission(n.perm)
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 fixed h-full z-30">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-slate-100">
          <div className="w-9 h-9 rounded-xl bg-indigo-600 grid place-items-center">
            <Wallet size={20} className="text-white" />
          </div>
          <span className="text-xl font-heading font-extrabold text-slate-900">SyncPe</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"
                }`
              }
            >
              <Icon size={19} strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center font-bold text-sm">
              {(user?.name || "U").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-slate-400 hover:text-rose-500 p-1">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0">
        {/* Top header */}
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-20 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 -ml-2" onClick={() => setMobileMenu((m) => !m)} data-testid="mobile-menu-btn">
              <Menu size={22} className="text-slate-700" />
            </button>
            <div className="md:hidden flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 grid place-items-center">
                <Wallet size={16} className="text-white" />
              </div>
              <span className="text-lg font-heading font-extrabold">SyncPe</span>
            </div>
            <div className="hidden md:block"><CompanySwitcher /></div>
          </div>
          <div className="flex items-center gap-3">
            <CurrencyToggle />
          </div>
        </header>

        {mobileMenu && (
          <div className="md:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setMobileMenu(false)}>
            <div className="bg-white w-72 h-full p-4 space-y-1" onClick={(e) => e.stopPropagation()}>
              <div className="mb-3"><CompanySwitcher /></div>
              {visibleNav.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to} onClick={() => setMobileMenu(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold ${
                      isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600"
                    }`}>
                  <Icon size={19} />{label}
                </NavLink>
              ))}
              <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-600 w-full">
                <LogOut size={19} />Logout
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 max-w-7xl w-full mx-auto min-w-0">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex z-30">
          {MOBILE_NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`bottomnav-${to.slice(1)}`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold ${
                  isActive ? "text-indigo-700" : "text-slate-400"
                }`}>
              {({ isActive }) => (<><Icon size={21} strokeWidth={isActive ? 2.5 : 2} /><span>{label.split(" ")[0]}</span></>)}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
